import { Component, ChangeDetectionStrategy, effect, inject, input, output, ChangeDetectorRef } from '@angular/core';
import deepmerge from 'deepmerge';
import { ReportInfo, ReportRecord, RecordUpdateEmission, TemplateProjectionMap } from '../../../models/report.model';
import { RawColumn } from '../../../models/template.model';
import { MAX_MEASUREMENNTOBJ, MINMAX_MEASUREMENTFIELDS } from '../data-view-helper';
import { LovService } from './lov-service';
import { NgClass, NgTemplateOutlet, TitleCasePipe } from '@angular/common';
import { TableModule } from 'primeng/table';
import { PrimeTemplate } from 'primeng/api';
import { MatIcon } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { L10nTranslationModule } from 'angular-l10n';

import { SelectTemplateOutletPipe } from '../../../shared/pipes/select-template-outlet.pipe';
import { DataViewFormatterPipe } from '../data-view-formatter.pipe';
import { DataViewRoutePipe } from '../data-view-route.pipe';
import { VERIFIED_DISABLED_FIELDS, vasApprovalFlag } from '../../../shared/constants/vas-disabled.const';
import { isCENotVASApproverFromStorage, isUKUserFromStorage } from '../../../shared/constants/has-user-group.const';
import { LeaseColumns } from '@miq/catalog';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'rep-data-view-table',
  templateUrl: './data-view-table.component.html',
  styleUrl: './data-view-table.component.scss',
  imports: [
    NgClass,
    TableModule,
    PrimeTemplate,
    MatIcon,
    RouterLink,
    NgTemplateOutlet,
    L10nTranslationModule,
    TitleCasePipe,
    SelectTemplateOutletPipe,
    DataViewFormatterPipe,
    DataViewRoutePipe,
  ],
})
export class DataViewTableComponent {
  private readonly lovService = inject(LovService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly isCENotVasApprover = isCENotVASApproverFromStorage();
  private readonly isUKUser = isUKUserFromStorage();

  isLovDataLoaded = false;

  reportRecordData: ReportRecord[];
  draggedRowIndex: number;
  allColumns: any[];
  currentFilteredField: RawColumn;
  actionsWord = {
    1: 'Actions',
    3: '操作',
  };
  actionHeader = this.actionsWord[1];
  showConfidentialityComponent = false;

  readonly reportData = input.required<ReportInfo>();
  readonly displayedColumns = input.required<RawColumn[]>();
  readonly reportLocale = input.required<number>();
  readonly defaultColumns = input.required<RawColumn[]>();
  readonly templateProjectionMap = input.required<TemplateProjectionMap>();

  readonly reportUpdateClick = output<RecordUpdateEmission>();
  readonly reportReordered = output<ReportRecord[]>();
  readonly removeRecord = output<ReportRecord>();
  readonly reorderReportColumns = output<number[]>();

  constructor() {
    // Subscribe to know success/failure
    this.lovService.loadAndStoreElementTypes().subscribe({
      next: () => {
        this.isLovDataLoaded = true;
        this.cdr.markForCheck();
      },
      error: () => {
        this.isLovDataLoaded = true;
        this.cdr.markForCheck();
      },
    });

    effect(() => {
      const reportData = this.reportData();
      const displayedColumns = this.displayedColumns();
      const defaultColumns = this.defaultColumns();
      const reportLocale = this.reportLocale();
      const templateProjectionMap = this.templateProjectionMap();

      this.reportRecordData = deepmerge([], reportData ? reportData.records : null);
      this.setMinMaxValueMeasurements();

      if (displayedColumns && displayedColumns.length) {
        this.allColumns = displayedColumns;
      } else if (defaultColumns) {
        this.allColumns = defaultColumns;
      }
      this.setMinMaxColumnMeasurements();
      this.actionHeader = this.actionsWord[reportLocale] ? this.actionsWord[reportLocale] : this.actionsWord[1];
      this.showConfidentialityComponent =
        !!templateProjectionMap && !!templateProjectionMap['miq-confidentiality-control-tmpl'];
    });
  }

  dragStartColumn(startIndex: number) {
    this.draggedRowIndex = startIndex;
  }

  dropColumn(droppedIndex: number) {
    if (this.draggedRowIndex !== droppedIndex && this.draggedRowIndex !== undefined) {
      // Update each item with new position
      const updatedData = this.reportRecordData.map((item, index) => ({
        ...item, // Keep all existing properties
        position: index + 1, // Add/update position
        recordId: item.recordId,
      }));

      // Update local data
      this.reportRecordData = updatedData;

      // Emit the updated data
      this.reportReordered.emit(updatedData);

      this.draggedRowIndex = undefined;
    }
  }

  emitEditableFields(event: MouseEvent, field: { [key: string]: any }, record: ReportRecord) {
    if (
      this.isCENotVasApprover &&
      VERIFIED_DISABLED_FIELDS.includes(field['field']) &&
      record.data?.['vas_approval_status_desc'] === vasApprovalFlag.verified
    )
      return;
    if (this.isUKUser && [LeaseColumns.LeaseCompDetails_TenantStatus].includes(field['columnId'])) return;
    if (field.parent.update) {
      let emittedObject: any;
      const edpUpdateConf = {
        labelMode: !!field.fieldUOM ? 'associated' : 'normal',
        updatableFields: [field.field, field.fieldUOM].filter(element => !!element),
      };
      emittedObject = {
        event,
        edpUpdateConf: edpUpdateConf,
        record: record.data,
      };
      if (emittedObject != null) {
        emittedObject.locale = this.reportLocale();
        this.reportUpdateClick.emit(emittedObject);
      }
    }
  }

  reorderColumns(columns: RawColumn[]) {
    const columnsIDs: number[] = columns.map(({ columnId }) => columnId);
    this.reorderReportColumns.emit(columnsIDs);
  }

  private setMinMaxColumnMeasurements(): void {
    // find if min or (and) max measurement column added and replace them  with custom range column
    // Convert MINMAX_MEASUREMENTFIELDS to a Set for efficient lookup
    const minMaxMeasurementFieldsSet = new Set(MINMAX_MEASUREMENTFIELDS);

    // Use a single pass to find the index and filter simultaneously
    let indexToReplace = -1;
    const filteredHeaders = [];
    this.allColumns.forEach((header, index) => {
      if (minMaxMeasurementFieldsSet.has(header.field)) {
        if (indexToReplace === -1) {
          indexToReplace = index;
        } // Only set the first time
      } else {
        filteredHeaders.push(header);
      }
    });

    // Update displayHeaders only if necessary
    if (indexToReplace !== -1) {
      filteredHeaders.splice(indexToReplace, 0, MAX_MEASUREMENNTOBJ);
      this.allColumns = filteredHeaders;
    }
  }

  private setMinMaxValueMeasurements(): void {
    // find if min value exists then set range along with UOM otherwise use only max value with UOM
    // Iterate over each recordDetailMeasure in searchResults
    this.reportRecordData?.forEach(recordDetailMeasure => {
      // Iterate over each measure in property_measurement
      recordDetailMeasure?.data['property_measurement']?.forEach(measure => {
        // Check if the min value exists
        if (measure['property_measurement_size_min']) {
          // Set range with UOM
          measure['property_measurement_size_max'] =
            `${measure['property_measurement_size_min']} - ${measure['property_measurement_size_max']} ${measure['property_measurement_size_max_uom']}`;
        } else if (measure['property_measurement_size_max']) {
          // Use only max value with UOM
          measure['property_measurement_size_max'] =
            `${measure['property_measurement_size_max']} ${measure['property_measurement_size_max_uom']}`;
        }
      });
    });
  }
}
